/**
 * Chat history — a full-screen, searchable grid of every saved conversation
 * (avatar pane + the /chat page both persist here). Search matches titles AND
 * message content server-side, so you can find anything ever said. Click a card
 * to read the full transcript in a side drawer.
 */
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";

import { api } from "@/lib/api";
import type { ChatConversation, ChatConversationMessage } from "@/lib/types";

export const Route = createFileRoute("/_auth/chat-history")({
  component: ChatHistoryPage,
});

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function ChatHistoryPage() {
  const qc = useQueryClient();
  const router = useRouter();
  const goBack = () => {
    if (window.history.length > 1) router.history.back();
    else router.navigate({ to: "/" });
  };
  const [rawSearch, setRawSearch] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(rawSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [rawSearch]);

  const { data: convs, isLoading } = useQuery({
    queryKey: ["chat-history", search],
    queryFn: () =>
      api<ChatConversation[]>(`/api/chat/conversations${search ? `?q=${encodeURIComponent(search)}` : ""}`),
  });

  const remove = async (id: string) => {
    await api(`/api/chat/conversations/${id}`, { method: "DELETE" });
    if (openId === id) setOpenId(null);
    qc.invalidateQueries({ queryKey: ["chat-history"] });
  };

  return (
    <div className="flex h-full min-h-0">
      {/* ── Grid ── */}
      <div className="flex-1 min-w-0 overflow-y-auto bg-slate-50 dark:bg-gray-950 px-6 py-6">
        <button
          onClick={goBack}
          className="mb-3 text-sm text-blue-600 hover:underline"
        >
          ← Back
        </button>
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Chat history</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Every conversation with the finance team. Search matches anything ever said.
            </p>
          </div>
          <input
            type="search"
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
            placeholder="Search all chats…"
            className="w-72 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {isLoading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : !convs || convs.length === 0 ? (
          <p className="text-sm text-slate-400">
            {search ? `No chats match "${search}".` : "No saved conversations yet."}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {convs.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setOpenId(c.id)}
                className={`group text-left rounded-xl border bg-white dark:bg-gray-900 p-4 transition-colors hover:border-blue-400 dark:hover:border-blue-500 ${
                  openId === c.id ? "border-blue-500 ring-1 ring-blue-500" : "border-slate-200 dark:border-gray-800"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 line-clamp-2">{c.title}</h3>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); void remove(c.id); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); void remove(c.id); } }}
                    title="Delete conversation"
                    className="shrink-0 rounded px-1 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-500"
                  >
                    ✕
                  </span>
                </div>
                {c.preview && (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 line-clamp-3">{c.preview}</p>
                )}
                <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-400">
                  <span>{fmtDate(c.updated_at)}</span>
                  {typeof c.message_count === "number" && <span>· {c.message_count} messages</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Transcript drawer ── */}
      {openId && <TranscriptDrawer id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function TranscriptDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: msgs, isLoading } = useQuery({
    queryKey: ["chat-history-msgs", id],
    queryFn: () => api<ChatConversationMessage[]>(`/api/chat/conversations/${id}/messages`),
  });

  return (
    <div className="w-[420px] max-w-[45vw] shrink-0 h-full flex flex-col border-l border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-gray-800">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Transcript</h2>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">✕</button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {isLoading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : !msgs || msgs.length === 0 ? (
          <p className="text-sm text-slate-400">No messages.</p>
        ) : (
          msgs.map((m) => (
            <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 dark:bg-gray-800 text-slate-800 dark:text-slate-100"
                }`}
              >
                <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:my-0.5">
                  <ReactMarkdown>{m.content}</ReactMarkdown>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
